import angular from 'angular';
import APIServiceModule from './APIService';
import TaskQueueServiceModule from './TaskQueueService';
import AppVersionServiceModule from './AppVersionService';
import analyticsServiceModule from './AnalyticsService';
import DataSync from '../model/DataSync';
import { createError } from '../error';
import { normalizeIdentifier } from '../util';
import { syncWindowDefinitions, defaultSyncWindowDefinition } from '../syncWindowDefinitions';
import { staffTokenNeedsRefresh } from '../staffToken';
import './';

const cachedCredentialsKey = 'credentials';
const jwtTokenKey = 'staff_login_jwt_token';
const syncWindowKey = 'sync_window';

class DataSyncService {
  constructor(
    $injector,
    $rootScope,
    $q,
    $timeout,
    APIService,
    AnalyticsService,
    TaskQueueService,
    AppVersionService,
    LogService,
    NewRelicService,
    DateTimeService,
    ApplicationCache
  ) {
    this.$injector = $injector;
    this.$rootScope = $rootScope;
    this.$q = $q;
    this.$timeout = $timeout;
    this.APIService = APIService;
    this.AnalyticsService = AnalyticsService;
    this.TaskQueueService = TaskQueueService;
    this.AppVersionService = AppVersionService;
    this.LogService = LogService;
    this.NewRelicService = NewRelicService;
    this.DateTimeService = DateTimeService;
    this.ApplicationCache = ApplicationCache;

    this._lastSyncStartedTime = null;
    this._lastSyncCompletedTime = null;
    this._lastSyncErrorTime = null;
    this._lastDataSync = null;
    this._lastRefreshStartedTime = null;
    this._lastRereshCompletedTime = null;
    this._lastRefreshErrorTime = null;
    this._syncInProgress = false;
    this._tokenRefreshPromise = null;
    this._tokenRefreshRefused = null;
    this._tokenGeneration = 0;
    this._refreshRequestedWhileOffline = false;

    // Track whether we need to sync
    this._needsSync = false;

    this._objectPromisesByType = {};
    this._multipleObjectPromisesByType = {};

    this._syncPromise = null;
    this._syncAgainPromise = null;
    this._refreshPositionsPromise = null;
    this._refreshPositionsAgainPromise = null;
    this._refreshDataPromise = null;
    this._refreshDataAgainPromise = null;
    this._loginPromise = null;
    this._logoutPromise = null;
    this._claimsPromise = null;

    this._notifyOfRefreshData = [];
    this._notifyOfRefreshPositions = [];
    this._notifyBeforeRefreshData = [];
    this._notifyBeforeRefreshPositions = [];

    this._syncWindowDefinition = defaultSyncWindowDefinition;

    this.APIService.addOnlineStatusListener(isOnline => {
      if (isOnline) {
        this.LogService.info("App came online; calling syncIfNeeded()");
        this.syncIfNeeded();
      } else {
        this.LogService.info("App went offline");
      }
    });
  }

  get StaffUserService() {
    if (!this._StaffUserService) {
      this._StaffUserService = this.$injector.get('StaffUserService');
    }
    return this._StaffUserService;
  }

  get isOnline() {
    return this.APIService.isOnline;
  }

  get numPendingTasks() {
    return this.TaskQueueService && this.TaskQueueService.pendingTasks.length || 0;
  }

  get isSyncNeeded() {
    if (this._needsSync) {
      // Always return true if this property is set
      return true;
    }
    const syncExpirationMS = 60 * 60 * 1000; // 60 minutes
    let needed;
    if (this.numPendingTasks > 0) {
      needed = true;
    } else if (!this._lastSyncCompletedTime) {
      needed = true;
    } else if (this.timeSinceLastSync >= syncExpirationMS) {
      needed = true;
    } else if (this._refreshRequestedWhileOffline) {
      needed = true;
    } else {
      needed = false;
    }
    this.LogService.info(`isSyncNeeded() returning ${needed}; pending tasks: ${this.TaskQueueService.pendingTasks.length} lastSyncCompleted: ${this._lastSyncCompletedTime} timeSinceLastSync: ${this.timeSinceLastSync}`);
    return needed;
  }

  get timeSinceLastSyncCompleted() {
    const now = new Date();
    return now - this._lastSyncCompletedTime;
  }

  get timeSinceLastSyncActivity() {
    let mostRecent = this._lastSyncCompletedTime;
    let refreshPositionsDate = this.StaffUserService.lastRefreshPositionsStartOrEndDate;
    if (refreshPositionsDate > mostRecent) {
      mostRecent = refreshPositionsDate;
    }
    let now = new Date();
    return now - mostRecent;
  }

  /**
   * @returns {null|DataSync}
   */
  get lastDataSync() {
    return this._lastDataSync;
  }

  get lastDataSyncUUID() {
    return this.lastDataSync && this.lastDataSync.uuid || null;
  }

  get isSyncActivityInProgress() {
    return this._syncInProgress || this.StaffUserService.isRefreshInProgress;
  }

  get syncPromise() {
    return this._syncPromise;
  }

  get refreshPositionsPromise() {
    return this._refreshPositionsPromise;
  }

  get refreshDataPromise() {
    return this._refreshDataPromise;
  }

  get loginPromise() {
    return this._loginPromise;
  }

  get logoutPromise() {
    return this._logoutPromise;
  }

  get claimsPromise() {
    return this._claimsPromise;
  }

  set claimsPromise(promise) {
    this._claimsPromise = promise;
  }

  set staffUser(user) {
    this.LogService && this.LogService.warn(`DataSyncService set staffUser deprecated! user:`, user)
      || console.warn(`DataSyncService set staffUser deprecated! user:`, user);
  }

  get staffUser() {
    return this.StaffUserService && this.StaffUserService.currentUser;
  }

  get staffUserId() {
    return this.staffUser && this.staffUser.id || this.StaffUserService.currentUserId;
  }

  get isLoggedIn() {
    return !!this.staffUser;
  }

  set loginUsername(username) {
    this._loginUsername = username;
  }

  get loginUsername() {
    return this._loginUsername;
  }

  get isDataSyncInProgress() {
    return !!this._syncInProgress;
  }

  get syncWindowDefinition() {
    return this._syncWindowDefinition;
  }

  set syncWindowDefinition(syncWindowDefinition) {
    if (syncWindowDefinition === this._syncWindowDefinition) {
      return;
    }
    this._syncWindowDefinition = syncWindowDefinition;
    this.setLastSyncWindowInApplicationCache(syncWindowDefinition.name);
    this.persistLastSyncWindowToBackend(syncWindowDefinition.name);
    if (this.staffUser) {
      this.staffUser.syncWindowDefinition = syncWindowDefinition;
    }
    this.$rootScope.$broadcast('syncWindowChanged', syncWindowDefinition);
  }

  get isSyncWindowSetToShowAll() {
    return this.syncWindowDefinition === this.syncWindowDefinitionAll;
  }

  get syncWindowDefinitionAll() {
    return syncWindowDefinitions.most;
  }

  setNeedsSync() {
    this._needsSync = true;
  }

  switchSyncWindowToShowAll() {
    this.syncWindowDefinition = this.syncWindowDefinitionAll;
  }

  switchSyncWindowToShowAllAndSync() {
    this.switchSyncWindowToShowAll();
    return this.sync();
  }

  addPreRefreshPositionsCallback(callback) {
    this._notifyBeforeRefreshPositions.push(callback);
  }

  addPreRefreshDataCallback(callback) {
    this._notifyBeforeRefreshData.push(callback);
  }

  addRefreshPositionsCallback(callback) {
    this._notifyOfRefreshPositions.push(callback);
  }

  addRefreshDataCallback(callback) {
    this._notifyOfRefreshData.push(callback);
  }

  syncIfNeeded() {
    // Determine if previous sync time is recent
    // TODO: Add API call to determine whether sync is necessary
    if (this.isSyncNeeded) {
      return this.sync();
    }
    return this.$q.when(null);
  }

  sync(config = null) {
    if (!this.APIService.isOnline) {
      this.LogService.error("Can't sync when offline!");
      this._syncRequestedWhileOffline = true;
      return this.$q.reject(createError({
        error: 'CannotRefreshWhileOffline',
        message: "Can't sync data while offline",
      }))
    }

    if (this._syncPromise) {
      if (!this._syncAgainPromise) {
        this._syncAgainPromise = this._syncPromise
          .then(() => this.$timeout(() => this.sync()))
          .finally(() => {
            this._syncAgainPromise = null;
          });
      }
      return this._syncAgainPromise;
    }

    const startTime = this.AnalyticsService.startEventTimer('SYNC');

    /*
     * Create DataSync object for tracking this sync, and eventually sending along to the server
     */
    const dataSync = new DataSync();
    dataSync.started_at = startTime;
    dataSync.staffing_users_id = this.staffUser && this.staffUser.id;
    dataSync.staff_app_version = this.AppVersionService.appVersion;
    dataSync.staff_app_version_full = this.AppVersionService.fullAppVersionInfo;
    dataSync.num_http_requests = 0;
    dataSync.total_bytes_sent = 0;
    dataSync.total_bytes_received = 0;
    const requestStatsCallback = (uri, method, status, duration, bytesSent, bytesReceived) => {
      dataSync.num_http_requests += 1;
      dataSync.total_bytes_sent += bytesSent;
      dataSync.total_bytes_received += bytesReceived;
    };

    this.LogService.info(`DataSyncService.sync() performing sync ${dataSync.uuid}`);

    this.$rootScope.$emit('syncStart');
    this._syncInProgress = true;
    this._syncPromise = this.$q.when(null)
      .then(() => this.refreshTokenIfStale())
      .then(() => this.getLastSyncWindowNameFromUserOrApplicationCache())
      .then(syncWindowName => {
        // Configure sync window
        if (syncWindowName && (syncWindowName in syncWindowDefinitions)) {
          this.syncWindowDefinition = syncWindowDefinitions[syncWindowName];
          this.LogService.debug(`DataSyncService.sync() updated syncWindowDefinition`);
        } else {
          this.LogService.debug(`DataSyncService.sync() unable to update syncWindowDefinition`);
        }
        dataSync.sync_window_definition = this.syncWindowDefinition;
        dataSync.sync_window_name = this.syncWindowDefinition && this.syncWindowDefinition.name;
      })
      .then(() => {
        this.APIService.setDataSyncUuidHeader(dataSync.uuid);
        this.APIService.addStatsCallback(requestStatsCallback);
        this.AnalyticsService.trackEvent('SYNC_START', {
          dataSyncUUID: dataSync.uuid,
        });
      })
      .then(() => this.processTasks())
      .then(num => {
        dataSync.num_offline_tasks_processed = num;
      })
      .then(() => this.refreshData())

      .then(() => {
        /*
         * Retrieve all recap definitions in order to complete delivery reporting (versions/timestamps) and include
         * in the DataSync object
         */
        this.LogService.info("DataSyncService finished refresh; gathering all recap definitions for delivery report");
        const recapDefinitionPromises = Object.values(this._objectPromisesByType['RecapDefinition'] || []);
        return this.$q.all(recapDefinitionPromises)
          .then(recapDefinitions => {
            recapDefinitions = recapDefinitions || [];
            dataSync.recap_definition_deliveries = recapDefinitions.map(recapDefinition => {
              return {
                events_recaps_id: recapDefinition.id,
                events_recaps_version: recapDefinition.version,
                events_recaps_updated_at: this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(recapDefinition.updated_at),
              };
            });
          })
          .catch(err => {
            // Swallow any error so that the data sync continues
            this.LogService.error("Error with recap definition reporting during sync:", err);
          });
      })

      .catch(err => {
        const timerInfo = this.AnalyticsService.stopTimer('SYNC');
        const duration = timerInfo && timerInfo.duration || null;
        this._lastSyncErrorTime = timerInfo && timerInfo.end || (new Date());
        dataSync.failed_at = this._lastSyncErrorTime;
        this.LogService.error("Error with sync:", err);
        dataSync.failed_at = this._lastSyncErrorTime;
        dataSync.duration = duration;
        this.AnalyticsService.trackEvent('SYNC', {
          duration,
          dataSync: this.serialize(dataSync),
        });
        return this.$q.reject(err);
      })
      .then(() => {
        this.markSyncCompleted();
        if (this.staffUser) {
          this.staffUser.removeAllEnqueuedClaims();
        }
      })
      .finally(() => {
        this._syncPromise = null;
        this._syncInProgress = false;
        this.LogService.info(`DataSyncService.sync() completed sync ${dataSync.uuid}`);
        const timerInfo = this.AnalyticsService.stopTimer('SYNC');
        const duration = timerInfo && timerInfo.duration || null;
        if (timerInfo) {
          dataSync.completed_at = timerInfo.end;
          dataSync.duration = timerInfo.duration;
        }
        this.AnalyticsService.trackEvent('SYNC', {
          duration,
          dataSync: this.serialize(dataSync),
        });
        this.APIService.removeStatsCallback(requestStatsCallback);
        this.APIService.removeDataSyncUuidHeader();
        this._lastDataSync = dataSync;
        this.$rootScope.$emit('syncComplete');
      });
    return this._syncPromise;
  }

  serialize(dataSync) {
    const data = {
      ...dataSync,
    };
    const timestampFields = [
      'started_at',
      'completed_at',
      'failed_at',
    ];
    timestampFields.forEach(timestampField => {
      data[timestampField] = dataSync[timestampField]
        ? this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(dataSync[timestampField])
        : null;
    });
    return data;
  }

  markSyncCompleted() {
    this._lastSyncTime = new Date();
  }

  processTasks() {
    this.LogService.log("DataSyncService.processTasks()");
    return this.TaskQueueService.executeAllTasks();
  }

  refreshData(config = null) {
    if (!this.APIService.isOnline) {
      this.LogService.error("Can't refresh data when offline!");
      this._refreshRequestedWhileOffline = true;
      return this.$q.reject(createError({
        error: 'CannotRefreshWhileOffline',
        message: "Can't refresh data while offline",
      }));
    }

    // Only allow refreshing data once
    if (this._refreshDataPromise) {
      if (!this._refreshDataAgainPromise) {
        this._refreshDataAgainPromise = this._refreshDataPromise
          .then(() => this.$timeout(() => this.refreshData(config)))
          .finally(() => {
            this._refreshDataAgainPromise = null;
          });
      }
      return this._refreshDataAgainPromise;
    }

    this.AnalyticsService.startEventTimer('REFRESH_DATA');
    this.LogService.info("REFRESHING DATA");
    this.$rootScope.$emit('refreshDataStarted');

    // Clone config object
    config = {...config};
    if (config.force === undefined) {
      config.force = true;
    }

    this.clearObjectPromises();
    this._refreshDataPromise = this.$q.when(null)
      .then(() => {
        this._notifyBeforeRefreshData.forEach(callback => callback())
      })
      .then(() => this.StaffUserService.refreshData(this.staffUser, {force: true}))
      .then(result => {
        this._notifyOfRefreshData.forEach(callback => callback(result));
        return result;
      })
      .finally(() => {
        this._refreshDataPromise = null;
        this._refreshRequestedWhileOffline = false;
        this.LogService.info("COMPLETED REFRESHING DATA");
        this.AnalyticsService.stopTimerAndTrackEvent('REFRESH_DATA');
        this.$rootScope.$emit('refreshDataComplete');
      });
    return this._refreshDataPromise;
  }

  refreshPositions(config = null) {
    if (!this.APIService.isOnline) {
      this.LogService.error("Can't refresh data when offline!");
      this._refreshRequestedWhileOffline = true;
      return this.$q.reject(createError({
        error: 'CannotRefreshWhileOffline',
        message: "Can't refresh data while offline",
      }));
    }

    // Only allow refreshing positions once
    if (this._refreshPositionsPromise) {
      if (!this._refreshPositionsAgainPromise) {
        this._refreshPositionsAgainPromise = this._refreshPositionsPromise
          .then(() => this.$timeout(() => this.refreshPositions(config)))
          .finally(() => {
            this._refreshPositionsAgainPromise = null;
          });
      }
      return this._refreshPositionsAgainPromise;
    }

    this.AnalyticsService.startEventTimer('REFRESH_POSITIONS');
    this.LogService.info("REFRESHING POSITIONS");
    this.$rootScope.$emit('refreshPositionsStart');

    // clone config object and set default force=true
    config = {...config};
    if (config.force === undefined) {
      config.force = true;
    }

    this.clearObjectPromises();
    this._refreshPositionsPromise = this.$q.when(null)
      .then(() => this._notifyBeforeRefreshPositions.forEach(callback => callback()))
      .then(() => this.StaffUserService.refreshPositions(this.staffUser, {force: true}))
      .then(result => {
        this._notifyOfRefreshPositions.forEach(callback => callback(result));
        return result;
      })
      .finally(() => {
        this._refreshPositionsPromise = null;
        this.LogService.info("COMPLETED REFRESHING POSITIONS");
        this.AnalyticsService.stopTimerAndTrackEvent('REFRESH_POSITIONS');
        this.$rootScope.$emit('refreshPositionsComplete');
      });
    return this._refreshPositionsPromise;
  }

  appVersionCheckAndUpdate() {
    return this.AppVersionService.versionCheckAndUpdate();
  }

  attemptLoginWithExistingCredentials() {
    this._loginPromise = this._readToken()
      .then(token => {
        if (token) return this.loginWithToken(token);
        return this.ApplicationCache.getItem(cachedCredentialsKey);
      })
      .then(credentials => {
        // If token login already returned a StaffUser, bubble it up
        if (credentials && credentials.id) return credentials;
        if (!credentials || !credentials.username) {
          return this.$q.reject(createError({
            error: 'NoCredentialsFound',
            message: "No credentials found in cache; cannot login",
          }));
        }
        const { username, password } = credentials;
        return this._doLoginWithoutClearingCache(username, password)
          .catch(err => {
            this.LogService.error("Error restoring previous login:", err);
            this.LogService.error("Wrapping as UnableToRetrieveCachedUserInfo");
            return this.$q.reject(createError({
              error: 'UnableToRetrieveCachedUserInfo',
              message: "Unable to retrieve user data",
              wrapped: err,
            }));
          })
          .then(result => {
            this.AnalyticsService.trackEvent('USER_LOGGED_IN_WITH_EXISTING_CREDENTIALS');
            return result;
          });
      });
    return this._loginPromise;
  }


  loginAndSync(username, password, config = null) {
    return this.login(username, password)
      .then(() => this.sync(config));
  }

  login(username, password) {
    this.AnalyticsService.startEventTimer('LOGIN');
    return this.ApplicationCache.clear()
      .then(() => this._doLoginWithoutClearingCache(username, password))
      .then(result => {
        this.AnalyticsService.trackEvent('USER_LOGGED_IN');
        this.AnalyticsService.stopTimerAndTrackEvent('LOGIN');
        return result;
      })
  }

  loginWithSamlRequestId(samlRequestId, config = null) {
    this._tokenGeneration += 1;
    const path = '/auth/login-saml';
    const data = {
      saml_request_id: samlRequestId,
    };
    return this.APIService.post(path, data)
      .catch(err => {
        this.LogService.error("Error exchanging SAML request ID for token:", err);
        return this.$q.reject(createError({
          error: 'UnableToExchangeRequestIDForToken',
          message: 'Error exchanging SAML request ID for authentication token',
        }))
      })
      .then(resp => {
        const data = resp.data;
        const token = data.token;
        if (token) {
          return this._persistToken(token)
            .then(() => this.loginWithToken(token, config));
        }

        this.LogService.error(`Response did not contain an auth token:`, resp);
        return this.$q.reject(createError({
          error: 'UnableToExchangeRequestIDForToken',
          message: 'Unable to exchange SAML request ID for authentication token',
        }));
      })
  }

  loginAndSyncWithSamlRequestId(samlRequestId, config = null) {
    return this.loginWithSamlRequestId(samlRequestId, config)
      .then(staffUser => {
        this.LogService.debug(`Successfully logged in using SAML request ID. Logged in as user:`, staffUser);
        this.LogService.debug(`Performing sync now...`);
        return this.sync(config);
      })
  }

  loginAndSyncWithToken(token, config = null) {
    return this.loginWithToken(token)
      .then(() => this.sync(config));
  }

  loginWithToken(token) {
    this._tokenGeneration += 1;
    this._syncInProgress = true;
    this.AnalyticsService.startEventTimer('LOGIN_WITH_TOKEN');
    this._loginPromise = this._persistToken(token)
      .then(() => this.StaffUserService.loginAndCreateViaToken(token))
      .then(staffUser => {
        this.AnalyticsService.trackEvent('USER_LOGGED_IN_WITH_TOKEN');
        this.AnalyticsService.stopTimerAndTrackEvent('LOGIN_WITH_TOKEN');
        return staffUser;
      })
      .finally(() => {
        this._loginPromise = null;
        this._syncInProgress = false;
      });
    return this._loginPromise;
  }

  _doLoginWithoutClearingCache(username, password) {
    // A credential login clears the default header but leaves APIService's
    // token in place until the new one arrives, so the token comparison in
    // refreshTokenIfStale() alone would let a refresh answering meanwhile
    // write the old session back.
    this._tokenGeneration += 1;
    this.loginUsername = username;
    this._syncInProgress = true;
    this._loginPromise = this.StaffUserService.loginAndCreate(username, password)
      .then(staffUser => {
        return this._storeCredentials(username, password)
          .then(() => staffUser);
      })
      .finally(() => {
        this._loginPromise = null;
        this._syncInProgress = false;
      });
    return this._loginPromise;
  }

  logout() {
    // Bumped before the logout request goes out, so a refresh that answers
    // while it is in flight cannot write its token back.
    this._tokenGeneration += 1;
    this._logoutPromise = this.StaffUserService.logout()
      .finally(logoutResult => {
        this.staffUser = null;
        return this._clearCredentials()
          .then(() => this._clearToken())
          .then(() => this.ApplicationCache.clear())
          .catch(err => {
            this.LogService.error("Swallowing error clearing ApplicationCache:", err);
          })
          .finally(() => logoutResult);
      })
    return this._logoutPromise;
  }

  _storeCredentials(username, password) {
    const credentials = {username, password};
    return this.ApplicationCache.setItem(cachedCredentialsKey, credentials);
  }

  _clearCredentials() {
    return this.ApplicationCache.removeItem(cachedCredentialsKey);
  }

  getObjectById(type, basePath, id, factory, config = null) {
    id = normalizeIdentifier(id);
    if (!(type in this._objectPromisesByType)) {
      this._objectPromisesByType[type] = {};
    }
    if (!(id in this._objectPromisesByType[type])) {
      const path = [basePath, id].join('/');
      this._objectPromisesByType[type][id] = this.APIService.get(path, config)
        .then(response => factory(response.data));
    }
    return this._objectPromisesByType[type][id];
  }

  getObjectByIdFromMultiple(type, path, id, factory, config = null) {
    config = config || {};
    id = normalizeIdentifier(id);
    if (!(type in this._objectPromisesByType)) {
      if (config.debug) {
        this.LogService.debug(`DataSyncService.getObjectByIdFromMultiple(${type}, ${path}, ${id}, ..) creating this._objectPromisesByType[${type}]`);
      }
      this._objectPromisesByType[type] = {};
    }
    if (!(id in this._objectPromisesByType[type])) {
      if (config.debug) {
        this.LogService.debug(`DataSyncService.getObjectByIdFromMultiple(${type}, ${path}, ${id}, ..) No object promise for id ${id}.. getting multiple`);
      }
      this._objectPromisesByType[type][id] = this.getMultipleObjects(type, path, factory, config)
        .then(objects => {
          let filtered = objects.filter(object => normalizeIdentifier(object.id) === id);
          let object = filtered.shift();
          if (!object) {
            this.LogService.warn(`getObjectByIdFromMultiple(${type}, ${path}, ${id}..) can not find id=${id} in response:`, objects);
          } else if (filtered.length > 1) {
            this.LogService.warn(`getObjectByIdFromMultiple(${type}, ${path}, ${id}..) Found multiple objects (${filtered.length}) with id=${id}:`, filtered, "all objects:", objects);
          }
          return object;
        })
        .catch(err => {
          this.LogService.error(`getObjectByIdFromMultiple(${type}, ${path}, ${id}, ...) error:`, err);
          return this.$q.reject(err);
        });
    } else {
      if (config.debug) {
        this.LogService.debug(`DataSyncService.getObjectByIdFromMultiple(${type}, ${path}, ${id}, ..) already have object promise for id ${id} in this._objectPromisesByType[${type}]`);
      }
    }
    return this._objectPromisesByType[type][id];
  }

  getLocalObjectById(type, id) {
    let objectPromise;
    if ((type in this._objectPromisesByType) && (id in this._objectPromisesByType[type])) {
      objectPromise = this._objectPromisesByType[type][id];
    } else {
      objectPromise = this.$q.when(null);
    }
    return objectPromise;
  }

  setObjectById(type, id, objectOrPromise) {
    id = normalizeIdentifier(id);
    if (!(type in this._objectPromisesByType)) {
      this._objectPromisesByType[type] = {};
    }
    this._objectPromisesByType[type][id] = this.$q.when(objectOrPromise);
    return this._objectPromisesByType[type][id];
  }

  haveMultipleObjectPromiseForTypeAndPath(type, path) {
    return (type in this._multipleObjectPromisesByType) && (path in this._multipleObjectPromisesByType[type]);
  }

  getLocalMultipleObjectPromiseForTypeAndPath(type, path) {
    if (this.haveMultipleObjectPromiseForTypeAndPath(type, path)) {
      return this._multipleObjectPromisesByType[type][path];
    } else {
      return this.$q.when(null);
    }
  }

  setObjectByIdForGetMultiple(type, path, id, objectOrPromise) {
    if (this.haveMultipleObjectPromiseForTypeAndPath(type, path)) {
      const replaceObjectInList = (objects, idToReplace, objectOrPromiseToReplaceWith) => {
        idToReplace = normalizeIdentifier(idToReplace);
        return objects.map(object => {
          if (normalizeIdentifier(object.id) === idToReplace) {
            return objectOrPromiseToReplaceWith;
          }
          return object;
        });
      };
      const oldGetMultiplePromise = this._multipleObjectPromisesByType[type][path];
      const newGetMultiplePromise = oldGetMultiplePromise
        .then(objects => replaceObjectInList(objects, id, objectOrPromise));
      this._multipleObjectPromisesByType[type][path] = newGetMultiplePromise;
      return newGetMultiplePromise;
    } else {
      this.LogService.warn(`DataSyncService.setObjectByIdForGetMultiple(${type}, ${path}, ${id}, ...) did not find existing promise for this type and path`);
      return this.$q.when(null);
    }
  }

  getMultipleObjects(type, path, factory, config = null) {
    let myConfig = config || {};
    let idField = myConfig.idField || 'id';
    if (!(type in this._multipleObjectPromisesByType)) {
      this._multipleObjectPromisesByType[type] = {};
    }
    if (!(path in this._multipleObjectPromisesByType[type])) {
      this._multipleObjectPromisesByType[type][path] = this.APIService.get(path, config)
        .then(response => {
          if (!response || !response.data) {
            this.LogService.error(`getMultipleObjects(${type}, ${path}) response has no data`);
            return [];
          }
          return response.data.map(objData => {
            objData.staffUser = this.staffUser;
            let obj = factory(objData);
            return this.$q.when(obj)
              .catch(err => {
                this.LogService.error(`getMultipleObjects(${type}, ${path}) error creating object with data:`, objData, "error:", err);
                return this.$q.reject(err);
              });
          });
        })
        .then(multiplePromises => this.setMultipleObjects(type, multiplePromises, idField));
    }
    return this._multipleObjectPromisesByType[type][path];
  }

  setMultipleObjects(type, objectsOrPromises, idField = 'id') {
    let setObjectPromises = objectsOrPromises.map(objectOrPromise => {
      return this.$q.when(objectOrPromise)
        .catch(err => {
          this.LogService.warn(`setMultipleObjects(${type}) swallowing error`, err);
          return null;
        })
        .then(object => {
          if (object && object[idField]) {
            let id = object[idField];
            return this.setObjectById(type, id, object)
          } else {
            return object;
          }
        });
    });
    return this.$q.all(setObjectPromises)
      // Filter out falsey values
      .then(results => results.filter(result => !!result));
  }

  clearObjectPromises() {
    this.LogService.debug("DataSyncService.clearObjectPromises()");
    this._objectPromisesByType = {};
    this._multipleObjectPromisesByType = {};
  }

  clearPromiseForObjectById(type, id) {
    if (type in this._objectPromisesByType) {
      if (id in this._objectPromisesByType[type]) {
        this._objectPromisesByType[type][id]
          .finally(result => {
            this.LogService.log(`Removed promise for object type=${type} id=${id}. Result was:`, result);
          });
        delete this._objectPromisesByType[type][id];
      }
    }
  }

  clearPromisesForMultipleObjectsOfType(type) {
    if (type in this._multipleObjectPromisesByType) {
      delete this._multipleObjectPromisesByType[type];
    } else {
      this.LogService.warn(`DataSyncService.clearPromisesForMultipleObjectsOfType(${type}) - promise not found`);
    }
  }

  isUpdateEnqueuedForObjectId(id, type) {
    return this.TaskQueueService.isUpdateEnqueuedForObjectId(id, type);
  }

  getLastSyncWindowNameFromUserOrApplicationCache() {
    if (this.StaffUserService.currentUser && this.StaffUserService.currentUser.syncWindowName) {
      return this.$q.when(this.StaffUserService.currentUser.syncWindowName);
    }
    return this.getLastSyncWindowNameFromApplicationCache();
  }

  getLastSyncWindowNameFromApplicationCache() {
    return this.ApplicationCache.getItem(syncWindowKey)
      .then(result => {
        return result;
      });
  }

  setLastSyncWindowInApplicationCache(syncWindowName) {
    return this.ApplicationCache.setItem(syncWindowKey, syncWindowName)
      .then(result => {
        return result;
      });
  }

  persistLastSyncWindowToBackend(syncWindowName) {
    const staffUserId = this.StaffUserService.currentUserId;
    if (staffUserId && syncWindowName) {
      const task = this.TaskQueueService.createTask('ChangeUserDataSyncWindowTask');
      task.staffUserId = staffUserId;
      task.dataSyncWindowName = syncWindowName;
      return this.TaskQueueService.executeOrEnqueue(task)
        .then(result => {
          this.LogService.info(`DataSyncService.persistLastSyncWindowToBackend() executeOrEnqueue(task) result:`, result);
        });
    } else {
      this.LogService.error(`DataSyncService.persistLastSyncWindowToBackend() cannot persist because missing staffUserId (${staffUserId}) or syncWindowName (${syncWindowName})`);
      return this.$q.when(null);
    }
  }

  /**
   * Exchanges the current token for a new one once it is a day old, so the
   * session slides instead of ending 30 days after login (#5489).
   *
   * Single-flight, because the server exchanges each token once and a second
   * concurrent call would be refused. Never rejects, so a sync never fails on
   * it: an unreachable server keeps the current token, a 403 (a switch-to
   * session, which cannot be extended) or a 401 stops further attempts with
   * that token, and the 401 leaves it for the next request to fail the way an
   * expired one always has.
   */
  refreshTokenIfStale() {
    if (this._tokenRefreshPromise) return this._tokenRefreshPromise;
    // Read from APIService rather than the cache: a username/password login
    // sets the token there without persisting it.
    const token = this.APIService.getAuthToken();
    const generation = this._tokenGeneration;
    if (!token || token === this._tokenRefreshRefused || !this.APIService.isOnline || !staffTokenNeedsRefresh(token)) {
      return this.$q.when(null);
    }
    // The token rides on this request explicitly, so a login that replaces the
    // default header while the request is being built cannot get its own new
    // token spent instead.
    this._tokenRefreshPromise = this.APIService.post('/auth/refresh', {}, { headers: { Authorization: 'Bearer ' + token } })
      .then(resp => {
        const rotated = resp.data && resp.data.token;
        if (!rotated) return null;
        // A logout or another login while the request was out owns the session
        // now; writing this token back would undo it. Checked in the same tick
        // as _persistToken() swaps the header, so nothing can run in between.
        if (generation !== this._tokenGeneration || this.APIService.getAuthToken() !== token) return null;
        return this._persistToken(rotated);
      })
      .catch(err => {
        if (err && (err.status === 401 || err.status === 403)) {
          this._tokenRefreshRefused = token;
        }
        this.LogService.warn('DataSyncService.refreshTokenIfStale() kept the current token:', err && err.status);
        return null;
      })
      .finally(() => {
        this._tokenRefreshPromise = null;
      });
    return this._tokenRefreshPromise;
  }

  _persistToken(token) {
    if (!token) return this.$q.when(null);
    // Make API immediately use it
    this.APIService.setAuthToken && this.APIService.setAuthToken(token);
    return this.ApplicationCache.setItem(jwtTokenKey, token);
  }

  _readToken() {
    return this.ApplicationCache.getItem(jwtTokenKey);
  }

  _clearToken() {
    this._tokenGeneration += 1;
    this.APIService.clearAuthToken && this.APIService.clearAuthToken();
    return this.ApplicationCache.removeItem(jwtTokenKey);
  }

}

const moduleName = 'recaps.services.DataSyncService';
let m = angular.module(moduleName, [
  'recaps.services',
  APIServiceModule.name,
  TaskQueueServiceModule.name,
  AppVersionServiceModule.name,
  analyticsServiceModule.name,
]);

m.service('DataSyncService', DataSyncService);

export default m;
